import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { newsAPI } from '../../services/api';
import { useSettings } from '../../context/SettingsContext';
import { formatNewsDate } from '../../utils/dateUtils';
import { ensureHttps } from '../../utils/urlUtils';
import './NewsPage.scss';

const NewsPage = () => {
  const { t, getLocalizedField, language } = useSettings();
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('grid'); 

  useEffect(() => {
    fetchData();
  }, [selectedCategory, searchQuery]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Загружаем категории только один раз
      if (categories.length === 0) {
        const categoriesResponse = await newsAPI.getCategories();
        const categoriesData = categoriesResponse.data?.results || categoriesResponse.data || [];
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      }

      // Загружаем новости с фильтрацией
      const params = {};
      if (selectedCategory) {
        params.category__slug = selectedCategory;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }

      const newsResponse = await newsAPI.getAll(params);
      const newsData = newsResponse.data?.results || newsResponse.data || [];
      setNews(Array.isArray(newsData) ? newsData : []);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Ошибка загрузки новостей');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="news-page">
      <div className="container">
        <div className="news-header">
          <h1 className="page-title">{t('newsTitle')}</h1>
          <p className="page-subtitle">
            {t('newsSubtitle')}
          </p>
        </div>

        <div className="news-controls">
          <div className="search-controls">
            <div className="search-box">
              <input
                type="text"
                placeholder={t('newsSearchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <span className="search-icon">🔍</span>
            </div>
            
            <div className="view-toggle">
              <button 
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Сетка"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 3H3V10H10V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 3H14V10H21V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 14H14V21H21V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 14H3V21H10V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button 
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="Список"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 6H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 6H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 12H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 18H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>

          <div className="category-filter">
            <button
              className={`category-chip ${!selectedCategory ? 'active' : ''}`}
              onClick={() => setSelectedCategory(null)}
            >
              {t('categoryAll')}
            </button>
            {Array.isArray(categories) && categories.map((category) => (
              <button
                key={category.id}
                className={`category-chip ${selectedCategory === category.slug ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.slug)}
              >
                {getLocalizedField(category, 'name')}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="loading">{t('newsLoading')}</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : !Array.isArray(news) || news.length === 0 ? (
          <div className="empty-state">
            <p>{t('newsNotFound')}</p>
          </div>
        ) : (
          <div className={`news-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
            {news.map((item) => (
              <Link
                to={`/news/${item.slug}`}
                key={item.id}
                className="news-card"
              >
                {item.cover_image && (
                  <div className="news-image">
                    <img src={ensureHttps(item.cover_image)} alt={getLocalizedField(item, 'title')} />
                    {item.is_breaking && (
                      <span className="breaking-badge">{t('breakingNews')}</span>
                    )}
                    {item.is_featured && (
                      <span className="featured-badge">{t('featuredNews')}</span>
                    )}
                  </div>
                )}
                
                <div className="news-content">
                  {item.category && (
                    <span className="news-category">{getLocalizedField(item.category, 'name')}</span>
                  )}
                  
                  <h3 className="news-title">{getLocalizedField(item, 'title')}</h3>
                  <p className="news-summary">{getLocalizedField(item, 'summary')}</p>
                  
                  <div className="news-meta">
                    <span className="news-date">
                      {formatNewsDate(item.published_at || item.created_at, language, t)}
                    </span>
                    <div className="news-stats">
                      <span>{t('viewsCount')} {item.views_count}</span>
                      <span>💬 {item.comments_count}</span>
                      <span>❤️ {item.likes_count}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsPage;
